import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {User} from '../../model/user';
import {SearchCriteria} from '../../model/search-criteria';
import {ConsultCriteria} from '../../model/consult-criteria';
import {Permissions} from '../../model/permissions';

/**
 * Service that handleChangeOfSelectFold the storage in browser, currently it's in local storage
 * it could be easily changed just modifying (putInStorage / PullFromStorage / removeElement)
 */
@Injectable()
export class StorageService {

  /**
   * the key for the storage of the userID
   */
  private localStorageUserID: string = environment.name + environment.userIdInStorage;
  private localStorageUserpwd: string = environment.name + environment.pwdInStorage;

  /**
   * the key for the storage of the token
   */
  private localStorageUserToken: string = environment.name + environment.tokenInStorage;
  private impersonatingToken: string = environment.name + environment.impersonatingToken;
  private impersonatingUser: string = environment.name + environment.impersonatingUser;

  /**
   * the key for the storage of the customerID
   */
  private localStorageUserCustomerID: string = environment.name + environment.customerIdInStorage;

  /**
   * the key for the storage of the language
   */
  private localStorageUserLanguage: string = environment.langInStorage;

  /**
   * the key for the storage of Searchcriterias
   */
  private localStorageUserSearchCriterias: string = environment.searchCriteriasInStorage;

  /**
   * the key for the storage of Consultcriterias
   */
  private localStorageUserConsultCriterias: string = environment.consultCriteriasInStorage;

  /**
   * the key for the storage of Adress datas
   */
  private localStorageAddressDatas: string = environment.addressDatasInStorage;

  /**
   * the key for the storage of fold data tables params
   */
  private localStorageFoldDatatablesParams: string = environment.foldDatatablesParamsInStorage;

  /**
   * the key for the storage of treatment chains
   */
  private localStoragetreatmentChains: string = environment.treatmentChainsInStorage;

  /**
   * the key for the storage of treatment chains
   */
  private localStorageAcceptedFiles: string = environment.acceptedFilesInStorage;


  /**
   * default constructor: do nothing
   */
  constructor() {
  }

  /**
   * Add content in storage using key
   * @param key - storage key
   * @param content - content that will be stringified
   */
  private putInStorage(key: string, content: any): void {
    localStorage.setItem(key, JSON.stringify(content));
  }

  /**
   * get content from storage using key
   * @param key - storage key
   * @return {any} - the parsed element in storage
   */
  private pullFromStorage(key: string): any {
    const value = localStorage.getItem(key);
    if (!value || value === 'undefined') {
      return null;
    }
    else {
      return JSON.parse(value);
    }
  }

  /**
   * remove element from storage using key
   * @param key - storage key
   */
  private removeElement(key: string): void {
    localStorage.removeItem(key);
  }

  /**
   * remove datas on logout (except rememberMe)
   */
  public clearStorage(): void {
    this.removeTokenFromStorage();
    // this.removeLangFromStorage();
    this.removeSearchCriteriasFromStorage();
    this.removeConsultCriteriasFromStorage();
    this.removeTreatmentChainsFromStorage();
    this.removeAddressDatasFromStorage();
    this.removeAcceptedFilesFromStorage();
    this.removeCurrentUserFromStorage();
    this.removeAzureLoginAttemptFromStorage();
    this.removeHasBeenWsoLoggedInFromStorage();
    //  will be removed when back-office connexion will be done
    // this.removeFoldDatatablesParamsFromStorage();
  }

  // ---------- Token ---------- //
  public getTokenFromStorage(): string {
    return this.pullFromStorage(this.localStorageUserToken);
  }
  public removeTokenFromStorage(): void {
    this.removeElement(this.localStorageUserToken);
  }

  public addTokenInStorage(token: string): void {
    this.putInStorage(this.localStorageUserToken, token);
  }
  public getImpersonatingTokenFromStorage(): string {
    return this.pullFromStorage(this.impersonatingToken);
  }

  public removeImpersonatingTokenFromStorage(): void {
    this.removeElement(this.impersonatingToken);
  }
  public addImpersonatingTokenFromStorage(token: string): void {
    this.putInStorage(this.impersonatingToken, token);
  }

  public getImpersonatingUserFromStorage(): User {
    return this.pullFromStorage(this.impersonatingUser);
  }

  public removeImpersonatingUserFromStorage(): void {
    this.removeElement(this.impersonatingUser);
  }
  public addImpersonatingUserFromStorage(user: User): void {
    this.putInStorage(this.impersonatingUser, user);
  }


  // ---------- Lang ---------- //
  public getLangFromStorage(): string {
    return this.pullFromStorage(this.localStorageUserLanguage);
  }

  public removeLangFromStorage(): void {
    this.removeElement(this.localStorageUserLanguage);
  }

  public addLangInStorage(token: string): void {
    this.putInStorage(this.localStorageUserLanguage, token);
  }

  // ---------- set Current User ---------- //
  public setCurrentUserInStorage(user: User): void {
    this.putInStorage('CURRENT_UISER', user);

  }
  // ---------- get Current User ---------- //
  public getCurrentUserInStorage(): User {

    return this.pullFromStorage('CURRENT_UISER');

  }
  public removeCurrentUserFromStorage(): void {
    this.removeElement('CURRENT_UISER');
  }

  public removeAzureLoginAttemptFromStorage(): void {
    sessionStorage.removeItem('AzureLoginAttempt');
  }

  public removeHasBeenWsoLoggedInFromStorage(): void {
    this.removeElement('hasBeenWsoLoggedIn');
  }

  public removeClientFromStorage(): void {
    this.removeElement('client');
  }

  public removeClientIDFromStorage(): void {
    this.removeElement('clientID');
  }

  // ---------- User ---------- //
  public addUserInStorage(user: User): void {
    this.putInStorage(this.localStorageUserCustomerID, user.idCustomer);
    this.putInStorage(this.localStorageUserID, user.idUser);
    //Le mot de passe ne doit pas être stocké, par conséquent, s'il est présent, on le supprime
    if(this.pullFromStorage(this.localStorageUserpwd)){
      this.removeElement(this.localStorageUserpwd);
    }
  }

  public getUserFromStorage(): User {
    const user = new User();
    user.idCustomer = this.pullFromStorage(this.localStorageUserCustomerID);
    user.idUser = this.pullFromStorage(this.localStorageUserID);
    return user;
  }

  public removeUserFromStorage(): void {
    this.removeElement(this.localStorageUserCustomerID);
    this.removeElement(this.localStorageUserID);
  }


  // ---------- Search Criterias ---------- //
  public addSearchCriteriasInStorage(criterias: SearchCriteria[]): void {
    this.putInStorage(this.localStorageUserSearchCriterias, criterias);
  }

  public getSearchCriteriasFromStorage(): SearchCriteria[] {
    return this.pullFromStorage(this.localStorageUserSearchCriterias);
  }

  public removeSearchCriteriasFromStorage(): void {
    this.removeElement(this.localStorageUserSearchCriterias);
  }


  // ---------- Consult Criterias ---------- //
  public addConsultCriteriasInStorage(criterias: ConsultCriteria[]): void {
    this.putInStorage(this.localStorageUserConsultCriterias, criterias);
  }

  public getConsultCriteriasFromStorage(): ConsultCriteria[] {
    return this.pullFromStorage(this.localStorageUserConsultCriterias);
  }

  public removeConsultCriteriasFromStorage(): void {
    this.removeElement(this.localStorageUserConsultCriterias);
  }


  // ---------- Adress Datas ---------- //
  public addAddressDatasInStorage(adress: any): void {
    this.putInStorage(this.localStorageAddressDatas, adress);
  }

  public getAddressDatasFromStorage(): any[] {
    return this.pullFromStorage(this.localStorageAddressDatas);
  }

  public removeAddressDatasFromStorage(): void {
    this.removeElement(this.localStorageAddressDatas);
  }


  // ---------- Fold DataTables Params ---------- //
  public addTempoFoldParamIntoStorage(user: User, foldDatatablesParams: any): void {
    this.putInStorage(this.localStorageFoldDatatablesParams + user.idUser + user.idCustomer, foldDatatablesParams);
  }

  public getTempoFoldParamFromStorage(user: User): any[] {
    return this.pullFromStorage(this.localStorageFoldDatatablesParams + user.idUser + user.idCustomer);
  }

  public removeFoldDatatablesParamsFromStorage(user: User): void {
    this.removeElement(this.localStorageFoldDatatablesParams + user.idUser + user.idCustomer);
  }


  // ---------- Treatment Chains ---------- //
  public addTreatmentChainsInStorage(treatmentChains: any) {
    this.putInStorage(this.localStoragetreatmentChains, treatmentChains);
  }


  public getTreatmentChainsFromStorage(): any[] {
    return this.pullFromStorage(this.localStoragetreatmentChains);

  }


  public removeTreatmentChainsFromStorage(): void {
    this.removeElement(this.localStoragetreatmentChains);
  }



  // ---------- Accepted files ---------- //
  public addAcceptedFilesInStorage(acceptedFiles: any) {
    this.putInStorage(this.localStorageAcceptedFiles, acceptedFiles);
  }

  public getAcceptedFilesFromStorage(): any {
    return this.pullFromStorage(this.localStorageAcceptedFiles);

  }

  public removeAcceptedFilesFromStorage(): void {
    this.removeElement(this.localStorageAcceptedFiles);
  }

  public getAutoRefreshFromStorage(customerId: string, userId: string): any {
    return this.pullFromStorage('AUTO_REFRESH_' + environment.name + '_' + customerId + '_' + userId);
  }
  public AddAutoRefreshToStorage(customerId: string, userId: string, value: boolean ): void {
    this.putInStorage('AUTO_REFRESH_' + environment.name + '_' + customerId + '_' + userId, value);
  }
}
